import api from "./api";
import logger from "../utils/logger";

const addressService = {
  /**
   * Get all addresses for the authenticated user
   * @returns {Promise<Array>} List of user addresses
   */
  getAddresses: async () => {
    try {
      const response = await api.get("/users/addresses");
      return response.data.data || response.data;
    } catch (error) {
      logger.error("Error fetching addresses", error);
      throw error;
    }
  },

  /**
   * Create a new address
   * @param {Object} addressData - Address data
   * @param {string} addressData.addressLine1 - Street address
   * @param {string} addressData.addressLine2 - Apt/Suite (optional)
   * @param {string} addressData.locality - City
   * @param {string} addressData.region - Province/State
   * @param {string} addressData.postcode - Postal code
   * @param {string} addressData.country - Country
   * @param {string} addressData.label - Label (optional, e.g., "Home", "Office")
   * @returns {Promise<Object>} Created address
   */
  createAddress: async (addressData) => {
    try {
      const response = await api.post("/users/addresses", addressData);
      return response.data.data || response.data;
    } catch (error) {
      logger.error("Error creating address", error);
      throw error;
    }
  },

  /**
   * Update an existing address
   * @param {number} addressId - Address ID
   * @param {Object} addressData - Updated address data
   * @returns {Promise<Object>} Updated address
   */
  updateAddress: async (addressId, addressData) => {
    try {
      const response = await api.patch(
        `/users/addresses/${addressId}`,
        addressData
      );
      return response.data.data || response.data;
    } catch (error) {
      logger.error("Error updating address", error);
      throw error;
    }
  },

  /**
   * Delete an address
   * @param {number} addressId - Address ID
   * @returns {Promise<void>}
   */
  deleteAddress: async (addressId) => {
    try {
      await api.delete(`/users/addresses/${addressId}`);
    } catch (error) {
      logger.error("Error deleting address", error);
      throw error;
    }
  },

  /**
   * Set an address as default
   * @param {number} addressId - Address ID
   * @returns {Promise<Object>} Updated address
   */
  setDefaultAddress: async (addressId) => {
    try {
      const response = await api.put(`/users/addresses/${addressId}/default`);
      return response.data.data || response.data;
    } catch (error) {
      logger.error("Error setting default address", error);
      throw error;
    }
  },
};

export default addressService;
